import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import themeGet from '@styled-system/theme-get';
import PropTypes from 'prop-types';

import Text from '../Text';
import { Button } from '../FormStyles';
import { Banner } from './BannerContainer';
import { useBannerColor } from './useBannerColor';

export default function WelcomeBanner({
  id,
  name,
  introMessage,
  primaryColor,
  handleOnClick,
}) {
  const { t } = useTranslation('header');

  const cookieName = `airbo-overlay-${id}=true`;
  const cookie = document.cookie.split('; ').find(el => el === cookieName);
  const [displayOverlay, setDisplayOverlay] = useState(null);
  const [welcomeBannerOpen, setWelcomeBannerOpen] = useState(true);

  const { textColor, boardColor } = useBannerColor({ primaryColor });

  useEffect(() => {
    if (!cookie) {
      setDisplayOverlay(true);
    } else {
      setDisplayOverlay(false);
    }
  }, [cookie]);

  const handleOverlayCookie = () => {
    if (!cookie) {
      document.cookie = cookieName;
      setDisplayOverlay(false);
    }
  };

  return (
    <>
      <Overlay
        data-testid="banner-toggle"
        displayed={displayOverlay}
        onClick={() => {
          setWelcomeBannerOpen(false);
          handleOverlayCookie();
        }}
      />
      <Wrapper
        displayOverlay={displayOverlay}
        onClick={() => {
          setWelcomeBannerOpen(true);
          handleOverlayCookie();
        }}
      >
        <Banner expanded={welcomeBannerOpen} boardColor={boardColor}>
          <HeaderText as="h3" variant="h3" color={textColor}>
            {name}
          </HeaderText>
          <IntroText
            expanded={welcomeBannerOpen}
            variant="body3"
            as="div"
            color={textColor}
          >
            {introMessage || ''}
          </IntroText>
          <ButtonContainer expanded={welcomeBannerOpen}>
            <WelcomeButton
              color={boardColor}
              backgroundColor={textColor}
              onClick={handleOnClick}
            >
              {t('welcomeModal.getStarted')}
              <ButtonIcon color={boardColor} />
            </WelcomeButton>
          </ButtonContainer>
        </Banner>
      </Wrapper>
    </>
  );
}

WelcomeBanner.propTypes = {
  handleOnClick: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string,
  introMessage: PropTypes.string,
  primaryColor: PropTypes.string,
};

const Overlay = styled.div`
  display: block;
  transition: opacity 0.8s, visibility 0.8s;
  visibility: ${props => (props.displayed ? 'visible' : 'hidden')};
  opacity: ${props => (props.displayed ? 1 : 0)};
  z-index: ${props => (props.displayed ? 3 : -1)};
  background-color: rgba(0, 0, 0, 0.4);
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const Wrapper = styled.div`
  z-index: ${({ displayOverlay }) => (displayOverlay ? 3 : 2)};
  margin: 24px;

  ${themeGet('mediaQueries.forExtraLargeUp')} {
    width: 1090px;
    margin: 24px auto 0;
  }
`;

const HeaderText = styled(Text)`
  margin: 3px 0 0;
  font-size: 20px;
  ${themeGet('mediaQueries.forLargeUp')} {
    margin: 0;
    font-size: 32px;
    width: 130%;
  }
`;

const transition = css`
  transition: max-height 0.5s ease-out, opacity 0.5s ease-out,
    margin 0.5s ease-out;

  max-height: ${({ expanded }) => (expanded ? '400px' : '0px')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};
  margin: ${({ expanded }) => (expanded ? '8px 0 0' : '0')};

  ${themeGet('mediaQueries.forLargeUp')} {
    margin: ${({ expanded }) => (expanded ? '16px 0 0' : '0')};
  }
`;

const ButtonContainer = styled.div`
  ${transition}
`;

const WelcomeButton = styled(Button)`
  color: ${props => props.color};
  border-color: ${props => props.color};
  background-color: ${props => props.backgroundColor};
  padding: 12px 18px;
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 10px;

  ${themeGet('mediaQueries.forLargeUp')} {
    padding: 15px 24px;
  }
  &:hover {
    color: ${props => props.color};
    background-color: ${props => props.backgroundColor};
    ${({ expanded }) => expanded && 'opacity: 0.9;'};
  }
  &:focus {
    color: ${props => props.color};
    background-color: ${props => props.backgroundColor};
  }
  &:active {
    color: ${props => props.color};
    background-color: ${props => props.backgroundColor};
  }
`;

const IntroText = styled(Text)`
  ${transition}
`;

const ButtonIcon = styled.i`
  color: ${props => props.color};
`;

ButtonIcon.defaultProps = {
  className: 'fa-solid fa-arrow-right fa-lg',
};
